import React, { useEffect, useState } from "react";
import { TextField, Button, Container, Grid, LinearProgress } from "@mui/material";
import Message from "./Message";
import { MessageDto } from "../models/MessageDto";

interface ChatProps {
  assistantId: string;
}

const Chat: React.FC<ChatProps> = ({ assistantId }) => {
  const [isWaiting, setIsWaiting] = useState<boolean>(false);
  const [messages, setMessages] = useState<Array<MessageDto>>(new Array<MessageDto>());
  const [input, setInput] = useState<string>("");
  const [assistant, setAssistant] = useState<any>(null);
  const [threadId, setThreadId] = useState<string | null>(null);

  useEffect(() => {
    initChatBot();
  }, []);

  const initChatBot = async () => {
    try {
      // Initialize the assistant and thread through Drupal backend
      const response = await fetch("/api/chatbot/init", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          assistantId: assistantId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.error) {
        console.error("Error initializing chatbot:", data.error);
        return;
      }

      setAssistant(data.assistant);
      setThreadId(data.threadId);

      // Set initial message
      setMessages([
        {
          content: `Hi, ich bin K.AI. Was möchten Sie über das Thema "${data.assistant.name}" wissen?`,
          isUser: false,
        },
      ]);

    } catch (error) {
      console.error("Failed to initialize chatbot:", error);
    }
  };

  const createNewMessage = (content: string, isUser: boolean): MessageDto => {
    return new MessageDto(isUser, content);
  };

  const handleSendMessage = async () => {
    if (!input.trim() || !threadId || !assistant) {
      return;
    }

    setIsWaiting(true);
    const userMessage = input.trim();

    // Add user message to UI immediately
    messages.push(createNewMessage(userMessage, true));
    setMessages([...messages]);
    setInput("");

    try {
      // Send message to Drupal backend
      const response = await fetch("/api/chatbot/message", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          message: userMessage,
          threadId: threadId,
          assistantId: assistant.id,
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.error) {
        console.error("Error sending message:", data.error);
        setMessages((prevMessages) => [
          ...prevMessages,
          createNewMessage("Entschuldigung, es gab einen Fehler bei der Verarbeitung Ihrer Nachricht.", false)
        ]);
        return;
      }

      // Add assistant response to UI
      setMessages((prevMessages) => [
        ...prevMessages,
        createNewMessage(data.response, false)
      ]);

      // Log the conversation to Drupal (optional)
      try {
        await fetch("/k_bot/message", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userMessage: userMessage,
            assistantResponse: data.response,
            threadId: threadId,
            runId: data.runId || null,
          }),
        });
      } catch (logError) {
        console.error("Fehler beim Senden an Drupal:", logError);
      }

    } catch (error) {
      console.error("Failed to send message:", error);
      setMessages((prevMessages) => [
        ...prevMessages,
        createNewMessage("Entschuldigung, der Service ist momentan nicht verfügbar.", false)
      ]);
    } finally {
      setIsWaiting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Container>
      <Grid container direction="column" spacing={2} paddingBottom={5}>
        {messages.map((message, index) => (
          <Grid item alignSelf={message.isUser ? "flex-end" : "flex-start"} key={index}>
            <Message key={index} message={message} />
          </Grid>
        ))}
        <Grid item id="kbot-input-grid">
          {!isWaiting && (
            <>
              <TextField
                id="kbot-user-input"
                label="Deine Nachricht"
                variant="outlined"
                disabled={isWaiting}
                fullWidth
                multiline
                maxRows={4}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSendMessage}
                disabled={isWaiting || !input.trim()}
                sx={{ mt: 1 }}
              >
                Senden
              </Button>
            </>
          )}
        </Grid>
        {isWaiting && (
          <Grid item>
            <LinearProgress color="inherit" />
          </Grid>
        )}
        <div id="kbot-disclaimer">
          K.AI gibt KI-generierte Antworten. Er kann Fehler machen. Überprüfen Sie seine Aussagen bevor Sie sie anwenden.
        </div>
      </Grid>
    </Container>
  );
};

export default Chat;
